// outbox-engine.js — the offline write ENGINE. It owns the single replay
// algorithm (drain()), the reachability + interval triggers, backoff, and the
// `opd:outbox` count events the UI badges off. It talks to the durable queue
// ONLY through the injected OutboxStore (so the verify harness runs it against an
// in-memory store) and to Medplum ONLY through the injected getMedplum() (so it
// replays as the logged-in user, RLS-scoped — never a service account,
// AGENTS.md §2 redesign).
//
// PLATFORM SEAM (W5 stage 2, EXPO_REPLATFORM_PLAN.md). This file has NO DOM
// (Document Object Model, the browser page API) globals: every browser touch the
// engine used to make inline — navigator.onLine, navigator.locks, window
// addEventListener('online'), window.dispatchEvent(CustomEvent) — is now a call
// on the injected `hooks` object, and the WEB implementations of those hooks live
// with the app (opd-app/src/lib/outbox.js), not here. Each hook is a real
// platform difference, not an abstraction for its own sake: React Native has no
// Web Locks, no `window` events and no navigator.onLine (it has NetInfo), and its
// fetch rejects with different error shapes. The hook contract is documented in
// this package's README; the defaults below are neutral and DOM-free so a host
// that supplies nothing still runs (single-context, always-online, silent).
//
// ONE write path online and offline (REDESIGN_ONLINE_PRIMARY.md): every queued
// write is enqueued the same way; online, we drain immediately so the caller
// still gets a real id synchronously; offline, the row sits 'queued' and the
// 'online' event / interval replays it later. Nothing is ever lost — a row
// leaves the queue only after it lands, or is parked for a human on a real
// conflict/non-retryable error.
//
// Replay is idempotent-by-construction: every create goes through
// createResourceIfNoneExist on the entry's slug identifier, so a lost
// resolvedId ack (crash between the POST landing and our patch) re-POSTs and
// Medplum returns the SAME resource — no twins on double-replay.
//
// enqueue() RETURN CONTRACT (relied on by medplum-callin.js and medplum-writes.js,
// and by the consult write batch built on top): enqueue() ALWAYS resolves to a
// TERMINAL SNAPSHOT of the row — { id, status, resolvedId, resolvedVersionId,
// lastError, … } — even when the row landed 'done' and was garbage-collected out
// of the queue in the SAME synchronous online drain (a 'done' create with no
// pending child, or any 'done' update, is deleted by _gcDone before enqueue()
// returns). The snapshot is captured at terminal-patch time inside _drainOnce
// (see _terminate) so a caller reads the real resolvedId (the server UUID) and
// resolvedVersionId (the server meta.versionId, for the next If-Match) with no
// second read. `status` is one of 'done' | 'queued' | 'conflict' | 'failed'; a
// genuinely offline enqueue returns the live 'queued' row. (Historically enqueue()
// returned `undefined` for the landed-and-GC'd case — every caller had to special-
// case that; the snapshot removes the trap that dropped a walk-in's chief
// complaint, M-6.)

import { getStatus } from '@medplum/core';

const MAX_ATTEMPTS = 8;         // dead-letter after this many transport failures
const BACKOFF_BASE_MS = 1000;   // 1s * 2^attempts, capped
const BACKOFF_CAP_MS = 30000;   // 30s ceiling
const IDLE_INTERVAL_MS = 20000; // re-drain cadence while the queue is non-empty
const DRAIN_LOCK_NAME = 'healther-opd-outbox-drain'; // cross-tab single-flight

/** The CustomEvent/emitter name the UI badges its pending-write count off. */
export const OUTBOX_COUNTS_EVENT = 'opd:outbox';

// ── Platform hooks (the ONLY seam to a host runtime) ─────────────────────────
// Neutral, DOM-free defaults: a host that supplies nothing behaves like a single
// JS context that is always online and has nowhere to broadcast. The web
// implementations (navigator.onLine / navigator.locks / window events / the
// fetch-TypeError network classifier) live in opd-app/src/lib/outbox.js. See the
// package README for the full contract.
export const NEUTRAL_PLATFORM_HOOKS = Object.freeze({
  /** Is the device believed to have connectivity right now? */
  isOnline: () => true,
  /** Is a thrown error a TRANSPORT failure (vs a real server rejection)? */
  isNetworkError: () => false,
  /** Broadcast an engine event to whatever the host's UI listens on. */
  emit: () => {},
  /** Subscribe to "connectivity came back"; return an unsubscribe fn, or null. */
  subscribeOnline: () => null,
  /**
   * Run `run` under a cross-context single-flight lock named `name`. If the lock
   * is already held elsewhere, return `onUnavailable()` instead of running.
   * A single-JS-context host correctly just calls `run()`.
   */
  withDrainLock: (name, run) => run(),
});

/** Default "was this throw a missing-session?" test, paired with getMedplum.
 *  Hosts that have a typed session error should pass a strict `instanceof`. */
const defaultIsNotLoggedIn = (e) => !!(e && e.name === 'NotLoggedInError');

// ── Error classification ──────────────────────────────────────────────────────
// Reuse the isPreconditionFailed shape from medplum-writes.js:48 (kept local to
// avoid importing that module's write helpers into the engine): a 412 means the
// optimistic If-Match lost a race → park 'conflict', do NOT retry.
function isPreconditionFailed(e) {
  return !!(e && (e.status === 412
    || /precondition/i.test(e.message || '')
    || /412/.test(String((e.outcome && e.outcome.id) || ''))));
}

// Recover the HTTP status of a thrown write error. The REAL @medplum/core client
// throws OperationOutcomeError — `class extends Error { constructor(outcome) }`
// with NO numeric `status`, and a message that is the outcome's human text
// ('Forbidden', 'Too Many Requests (…)', 'Precondition Failed'), never a 3-digit
// code. So we FIRST map the carried FHIR OperationOutcome to its status via
// @medplum/core's getStatus (forbidden→403, too-many-requests→429,
// business-rule/invalid→422/400, precondition-failed→412, exception/timeout→5xx),
// mirroring how isPreconditionFailed above special-cases 412 off the outcome.
// Only with no outcome do we fall back to a raw fetch-level numeric `status`, then
// to a code embedded in the message. Returns 0 when nothing identifies a status (a
// bare network/offline failure) → isTransient treats that as transient.
//
// This is load-bearing: reading only `e.status`/the message regex returned 0 for
// EVERY real Medplum 4xx, so a genuine 403/422 rejection was mis-classified as
// transient and silently re-queued instead of parked 'failed' (M-7 / weakened
// M-6); a real 429 backed off only by the no-status accident, not the 429 branch.
function statusOf(e) {
  if (!e) return 0;
  if (e.outcome) return getStatus(e.outcome);
  if (typeof e.status === 'number') return e.status;
  const m = /\b(4\d\d|5\d\d)\b/.exec(String(e.message || ''));
  return m ? Number(m[1]) : 0;
}

// A 5xx or a bare network failure (no HTTP status) is transient → backoff.
// A non-412 4xx is the client's own bad request → park 'failed', never retry
// (retrying a 422 just dead-letters slower and hides the bug — fail loud).
// EXCEPT 429 (Too Many Requests) and 408 (Request Timeout): those are transient
// server-side back-pressure / a timed-out request, and a backoff-and-retry is the
// correct response, not a permanent park (m-25). Every other 4xx stays permanent.
function isTransient(e) {
  const s = statusOf(e);
  if (s >= 500) return true;
  if (s === 429 || s === 408) return true; // rate-limit / timeout → back off + retry
  if (s >= 400) return false; // other 4xx (incl. 422) — non-retryable
  return true;                // no status → network/offline → transient
}

function outcomeText(e) {
  if (!e) return null;
  const oo = e.outcome;
  if (oo && Array.isArray(oo.issue) && oo.issue.length) {
    const i = oo.issue[0];
    return (i.details && i.details.text) || i.diagnostics || i.code || e.message || null;
  }
  return e.message || String(e);
}

function backoffMs(attempts) {
  const raw = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempts);
  const jitter = raw * 0.2 * (Math.random() * 2 - 1); // ±20%
  return Math.max(0, Math.round(raw + jitter));
}

// ── Local-ref rewriting ───────────────────────────────────────────────────────
// A queued body may reference a sibling that hasn't been created yet via
// 'urn:local:<uuid>'. Once that sibling lands, refMap maps its localRef → the
// real 'ResourceType/<id>'. rewriteLocalRefs deep-clones the body replacing
// every such string, so the POSTed body carries only real references.
function rewriteLocalRefs(body, refMap) {
  if (body == null) return body;
  if (typeof body === 'string') {
    return Object.prototype.hasOwnProperty.call(refMap, body) ? refMap[body] : body;
  }
  if (Array.isArray(body)) return body.map((v) => rewriteLocalRefs(v, refMap));
  if (typeof body === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(body)) out[k] = rewriteLocalRefs(v, refMap);
    return out;
  }
  return body;
}

// Does the (possibly-rewritten) body still contain any unresolved local ref?
function hasUnresolvedLocalRef(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.startsWith('urn:local:');
  if (Array.isArray(value)) return value.some(hasUnresolvedLocalRef);
  if (typeof value === 'object') return Object.values(value).some(hasUnresolvedLocalRef);
  return false;
}

/**
 * The offline write outbox. Construct with a store (any OutboxStore — the app's
 * IndexedDB or SQLCipher adapter, or the in-memory one the harness injects), a
 * getMedplum() that yields the LOGGED-IN user's client, and the host's platform
 * hooks. All triggers are wired by start().
 *
 * @param {object}   o
 * @param {object}   o.store          an OutboxStore (see the package README)
 * @param {Function} o.getMedplum     async () => ({ medplum }) as the live user
 * @param {object}  [o.hooks]         platform hooks; defaults to NEUTRAL_PLATFORM_HOOKS
 * @param {Function}[o.isNotLoggedIn] (e) => boolean — is this throw "no session"?
 */
export class Outbox {
  constructor({ store, getMedplum, hooks, isNotLoggedIn } = {}) {
    if (!store) throw new Error('Outbox: a store is required');
    if (typeof getMedplum !== 'function') throw new Error('Outbox: a getMedplum is required');
    this.store = store;
    this.getMedplum = getMedplum;
    this.hooks = { ...NEUTRAL_PLATFORM_HOOKS, ...(hooks || {}) };
    this.isNotLoggedIn = isNotLoggedIn || defaultIsNotLoggedIn;
    this._draining = false;   // single-flight guard
    this._interval = null;
    this._offOnline = null;   // unsubscribe fn from hooks.subscribeOnline, or null
    // Terminal snapshots captured during the CURRENT drain (id → row snapshot),
    // so enqueue()/coalesce()/retry() can still report the outcome of a row that
    // landed 'done' and was GC'd out of the queue in that same drain. Cleared at
    // the start of every top-level drain (see run() in drain()).
    this._terminalSnapshots = new Map();
  }

  // ── enqueue ─────────────────────────────────────────────────────────────────
  // Assign the monotonic seq, persist, fire counts. When online, drain
  // immediately so the caller can get the real id synchronously (the online
  // path awaits enqueue → drain → reads resolvedId). Offline, the row waits.
  async enqueue(entry) {
    const seq = await this.store.nextSeq();
    const row = { ...entry, seq, status: entry.status || 'queued' };
    await this.store.put(row);
    await this._fireCounts();
    if (this.hooks.isOnline()) {
      try {
        await this.drain();
      } catch (ex) {
        // The row is durably persisted above, so a TRANSPORT failure during
        // this eager drain must not surface as a write failure to the caller
        // — isOnline() is a lying hint (Capacitor WebViews keep navigator.onLine
        // true in airplane mode; a LAN can be up with the server down), and the
        // online/interval triggers retry. The caller sees the honest row
        // status ('queued'). Anything that is NOT a transport failure is a
        // real engine bug and still propagates (§1.3 fail loud).
        if (!this.hooks.isNetworkError(ex)) throw ex;
      }
    }
    // Terminal-snapshot aware: a row that landed 'done' on the eager online drain
    // was GC'd out of the store, so store.get() is undefined — return the snapshot
    // captured at terminal-patch time instead (the enqueue() return contract).
    return this._terminalRow(row.id);
  }

  // The still-present row, or — when it landed and was GC'd in this drain — the
  // terminal snapshot captured at patch time. undefined only when the id was never
  // processed at all (a genuinely offline enqueue returns its live 'queued' row).
  async _terminalRow(id) {
    const live = await this.store.get(id);
    if (live) return live;
    return this._terminalSnapshots.get(id) || undefined;
  }

  // Patch a row to a TERMINAL status AND record its snapshot for this drain, so a
  // caller that reads back after a GC still sees the outcome. `extra` carries
  // non-column derived data (resolvedVersionId — the server meta.versionId).
  async _terminate(id, changes, extra) {
    const merged = await this.store.patch(id, changes);
    this._terminalSnapshots.set(id, extra ? { ...merged, ...extra } : merged);
    return merged;
  }

  // ── coalesce a re-save into a still-queued create ────────────────────────────
  // A caller that re-saves the SAME logical resource (same conditional-create
  // slug) before its first queued create has drained would otherwise pile up a
  // SECOND create entry. Both are idempotent on the slug (createResourceIfNoneExist
  // collapses them to one resource), but the LATER body would be silently dropped
  // (If-None-Exist returns the first-created resource without applying the new
  // body). The consult-draft first-save (medplum-writes.js) sends the FULL draft
  // each time, so instead of a second create we replace the queued entry's body
  // with the newest one — the latest offline draft lands on the FIRST reconnect.

  /** The still-QUEUED create entry for a conditional-create slug, or null. Only
   *  'queued' (not 'inflight') is coalescable: an inflight row may be mid-write. */
  async findPendingCreate(slugSystem, slug) {
    const all = await this.store.allOrdered();
    return all.find((e) => e.op === 'create' && e.status === 'queued'
      && e.slugSystem === slugSystem && e.slug === slug) || null;
  }

  /** Replace a queued create entry's body with a newer one, then (when online)
   *  drain so it lands immediately. Returns the (possibly now-drained) row. */
  async coalesceCreateBody(id, body) {
    await this.store.patch(id, { body });
    if (this.hooks.isOnline()) {
      try {
        await this.drain();
      } catch (ex) {
        // Same rationale as enqueue(): a transport failure on the eager drain is
        // not a caller error (the row is durably persisted); anything else is a
        // real engine bug and propagates (§1.3 fail loud).
        if (!this.hooks.isNetworkError(ex)) throw ex;
      }
    }
    return this._terminalRow(id);
  }

  // ── drain(): the replay algorithm ────────────────────────────────────────────
  // In-process _draining guards re-entrancy within one tab; the host's
  // withDrainLock hook (Web Locks in a browser) ensures only one tab drains at a
  // time — a second tab skips its cycle rather than racing updates/backoff. A
  // host with a single JS context (Node harness, React Native) passes the run
  // through, which is exactly the original no-navigator.locks behaviour.
  async drain() {
    if (this._draining) return { landed: 0, skipped: true };

    const run = async () => {
      this._draining = true;
      // Fresh snapshot set per top-level drain — bounds the Map to this drain's
      // terminal rows (the "unblock siblings" recursion inside _drainOnce does NOT
      // clear it, so snapshots from an earlier pass survive into that recursion).
      this._terminalSnapshots.clear();
      try {
        return await this._drainOnce();
      } finally {
        this._draining = false;
      }
    };

    return this.hooks.withDrainLock(
      DRAIN_LOCK_NAME,
      () => {
        // Re-checked INSIDE the lock: acquiring a real cross-tab lock is async,
        // so another caller may have begun draining while we waited. (On the
        // pass-through path nothing has awaited, so this is a no-op.)
        if (this._draining) return { landed: 0, skipped: true };
        return run();
      },
      () => ({ landed: 0, skipped: true }),
    );
  }

  async _drainOnce() {
    // (1) AUTH GATE. No live session → return, queue intact (the 'online' event
    // or the next navigation retries). getMedplum() throws NotLoggedInError.
    let medplum;
    let liveProfileId = null;
    try {
      const session = await this.getMedplum();
      medplum = session.medplum;
      const p = medplum.getProfile ? medplum.getProfile() : null;
      liveProfileId = p ? p.id : null;
    } catch (e) {
      if (this.isNotLoggedIn(e)) return { landed: 0, notLoggedIn: true };
      throw e;
    }

    // (1b) STALE-INFLIGHT SWEEP (O2). A row is patched 'inflight' at step (4)
    // just before the write; if the tab crashes/closes between that patch and
    // the terminal patch, the row is stranded 'inflight' forever — the FIFO loop
    // only actions status==='queued', so it never replays and no badge surfaces
    // it. There is exactly ONE single-flight drain per Outbox instance (the
    // _draining guard), so any row already 'inflight' at drain-START cannot be a
    // live write from this pass — it must be stale from a crashed prior run.
    // Revert it to 'queued'; replay is idempotent by construction (creates go
    // through createResourceIfNoneExist on the slug; updates carry If-Match), so
    // re-sending a write that may have already landed cannot twin or clobber.
    for (const e of await this.store.byStatus('inflight')) {
      await this.store.patch(e.id, { status: 'queued' });
    }

    // (2) FIFO by seq. Build refMap from already-done entries so a resume run
    // still resolves children whose parents landed in an earlier pass/process.
    const all = await this.store.allOrdered();
    const refMap = {};
    for (const e of all) {
      if (e.status === 'done' && e.localRef && e.resolvedId) {
        refMap[e.localRef] = `${e.resourceType}/${e.resolvedId}`;
      }
    }

    // Different-profile guard: an entry enqueued by another user must NEVER be
    // replayed as the live user (would write one clinician's data under
    // another's identity/RLS). Park it 'failed' with a clear reason.
    for (const e of all) {
      if (e.status === 'queued' && e.profileId && liveProfileId && e.profileId !== liveProfileId) {
        await this.store.patch(e.id, {
          status: 'failed',
          // Distinguishable park reason: this row is NOT retryable by the current
          // user (a retry just re-parks it here). The sync popover keys off this
          // code to hide the Retry button and explain WHY, offering only Discard
          // (m-26). Discard is destructive, so it stays confirm-gated in the UI.
          lastErrorCode: 'different-user',
          lastError: 'queued by a different user — not replayed under the current session',
        });
      }
    }

    let landed = 0;
    const landedTypes = new Set();
    const now = Date.now();

    // Re-read after the different-profile parking so we iterate fresh statuses.
    const rows = (await this.store.allOrdered());

    for (const entry of rows) {
      // (3) ACTIONABLE = queued AND its backoff has elapsed AND every parentRef
      // is resolved in refMap.
      if (entry.status !== 'queued') continue;
      if ((entry.nextAttemptAt || 0) > now) continue;
      const parentsResolved = (entry.parentRefs || []).every((r) => refMap[r]);
      if (!parentsResolved) continue;

      // (4) mark inflight, then write.
      await this.store.patch(entry.id, { status: 'inflight' });
      try {
        if (entry.op === 'create') {
          const body = rewriteLocalRefs(entry.body, refMap);
          if (hasUnresolvedLocalRef(body)) {
            // A ref we expected to resolve didn't — revert to queued, try next pass.
            await this.store.patch(entry.id, { status: 'queued' });
            continue;
          }
          // createResourceIfNoneExist on the slug identifier — the anti-twin
          // guard AND the idempotent double-replay guarantee in one call.
          const query = 'identifier=' + entry.slugSystem + '|' + entry.slug;
          const saved = await medplum.createResourceIfNoneExist(body, query);
          // (5) success → done + resolvedId; update refMap IMMEDIATELY so a
          // sibling later in THIS same pass sees the real id. Snapshot the
          // terminal row (with the server versionId) so enqueue() can report the
          // real id even after this 'done' row is GC'd in this same drain.
          await this._terminate(entry.id,
            { status: 'done', resolvedId: saved.id, lastError: null },
            { resolvedVersionId: saved && saved.meta && saved.meta.versionId });
          if (entry.localRef) refMap[entry.localRef] = `${entry.resourceType}/${saved.id}`;
          landed += 1;
          landedTypes.add(entry.resourceType);
        } else {
          // op:'update' → If-Match W/"<versionId>" via updateResource.
          const body = rewriteLocalRefs(entry.body, refMap);
          const headers = entry.ifMatch ? { 'If-Match': 'W/"' + entry.ifMatch + '"' } : undefined;
          const saved = await medplum.updateResource(body, headers ? { headers } : undefined);
          // Snapshot the server-assigned versionId so the caller refreshes its
          // If-Match to the FRESH version (m-29 — a stale version returned here
          // 412s the very next save/sign with no recovery but a reload).
          await this._terminate(entry.id,
            { status: 'done', resolvedId: (saved && saved.id) || entry.targetId, lastError: null },
            { resolvedVersionId: saved && saved.meta && saved.meta.versionId });
          landed += 1;
          landedTypes.add(entry.resourceType);
        }
      } catch (e) {
        if (isPreconditionFailed(e)) {
          // (6) 412 → conflict (the offline analogue of DraftConflictError /
          // PatientConflictError). Server copy is intact; a human reconciles.
          await this._terminate(entry.id, { status: 'conflict', lastError: outcomeText(e) });
        } else if (!isTransient(e)) {
          // (7) non-retryable 4xx (e.g. 422) → failed, no retry. Fail loud.
          await this._terminate(entry.id, { status: 'failed', lastError: outcomeText(e) });
        } else {
          // (8) 5xx / network → backoff; dead-letter at the attempts cap.
          const attempts = (entry.attempts || 0) + 1;
          if (attempts >= MAX_ATTEMPTS) {
            await this._terminate(entry.id, {
              status: 'failed', attempts, lastError: 'dead-lettered after ' + attempts + ' attempts: ' + outcomeText(e),
            });
          } else {
            await this.store.patch(entry.id, {
              status: 'queued', attempts, nextAttemptAt: Date.now() + backoffMs(attempts), lastError: outcomeText(e),
            });
          }
        }
      }
    }

    // (9) GC: delete 'done' rows no longer referenced by any pending parentRefs
    // (a landed parent must survive until every child that references it lands).
    await this._gcDone();

    // Fire counts + invalidate search caches on landed types so the board/read
    // paths see the new rows.
    await this._fireCounts();
    if (medplum.invalidateSearches) {
      for (const t of landedTypes) medplum.invalidateSearches(t);
    }

    // (10) Re-drain once if something landed and queued rows remain (a landed
    // parent may have unblocked a child whose backoff has now elapsed). Call
    // _drainOnce directly — we already hold the single-flight guard, so calling
    // drain() would short-circuit on _draining. This is the "unblock siblings"
    // pass, not a spin loop: an actionable child now has its parent in refMap,
    // and a pass with nothing newly actionable lands 0 and stops the recursion.
    if (landed > 0) {
      const stillQueued = await this.store.byStatus('queued');
      const actionableNow = stillQueued.some((e) => (e.nextAttemptAt || 0) <= Date.now());
      if (actionableNow) {
        const again = await this._drainOnce();
        landed += again.landed || 0;
      }
    }

    return { landed };
  }

  // A 'done' entry is safe to delete once NO other still-present entry (ANY
  // non-done status — queued/inflight/conflict AND failed) references its
  // localRef in parentRefs. A failed child is not terminal: it can be re-queued
  // by a human, and on that retry it needs its parent's real id back in refMap.
  // If we GC'd the parent while the child sat 'failed', the child's urn:local:
  // parentRef could never resolve again (refMap is rebuilt only from surviving
  // 'done' rows) → the write is lost forever (O1). So keep the parent alive
  // until every referencing entry is itself 'done'.
  async _gcDone() {
    const all = await this.store.allOrdered();
    const pendingRefs = new Set();
    for (const e of all) {
      if (e.status !== 'done') {
        for (const r of e.parentRefs || []) pendingRefs.add(r);
      }
    }
    for (const e of all) {
      if (e.status === 'done' && (!e.localRef || !pendingRefs.has(e.localRef))) {
        await this.store.remove(e.id);
      }
    }
  }

  // ── counts + parked reads (the chip's PUBLIC read API) ──────────────────────
  // The sync chip mounts on screens that may never see an `opd:outbox` event
  // (consult/prescription run no drain) and, on native, its queue lives in the
  // SQLite store the chip can't reach directly. getCounts()/getParked() let the
  // chip read the PLATFORM-CORRECT store through the singleton, so a fresh mount
  // reflects the real queue instead of a hardcoded IndexedDB read that misses the
  // native queue entirely.

  /** Current counts by status, read from THIS engine's own (platform-correct) store. */
  async getCounts() {
    const all = await this.store.allOrdered();
    const counts = { queued: 0, inflight: 0, conflict: 0, failed: 0, done: 0 };
    for (const e of all) counts[e.status] = (counts[e.status] || 0) + 1;
    return counts;
  }

  /** Parked entries (failed + conflict) in FIFO order — the rows the popover
   *  offers Retry/Discard on. Full entry shape so the UI can show resourceType/
   *  slug/createdAt/lastError. */
  async getParked() {
    const failed = await this.store.byStatus('failed');
    const conflict = await this.store.byStatus('conflict');
    return [...failed, ...conflict].sort((a, b) => (a.seq || 0) - (b.seq || 0));
  }

  // ── counts event ──────────────────────────────────────────────────────────
  // Emit the `opd:outbox` event the UI badges off (a window CustomEvent on the
  // web — see the host's emit hook). Counts by status so a badge can show queued
  // (pending sync) distinctly from parked (needs a human).
  async _fireCounts() {
    const counts = await this.getCounts();
    this.hooks.emit(OUTBOX_COUNTS_EVENT, counts);
    return counts;
  }

  // ── dead-letter actions (the popover's Retry / Discard) ─────────────────────
  // A row parked 'failed' (dead-lettered after MAX_ATTEMPTS transport failures,
  // or a non-retryable 4xx) or 'conflict' (412) needs a human surface. These two
  // methods are what the sync-status popover calls.

  /**
   * Re-queue a parked row for another replay: clear the backoff/attempt history
   * and lastError, then kick a drain. Replay stays idempotent-by-construction
   * (creates go through createResourceIfNoneExist on the slug; updates carry
   * If-Match), so re-sending a write that may already have landed cannot twin.
   * NOTE: the UI only offers Retry on 'failed' rows — a 'conflict' (stale
   * If-Match) would just re-fail, so its popover row offers Discard only.
   */
  async retry(id) {
    const row = await this.store.get(id);
    if (!row) return null;
    await this.store.patch(id, { status: 'queued', attempts: 0, nextAttemptAt: 0, lastError: null });
    await this._fireCounts();
    // Best-effort immediate replay. A transport failure here is expected (the
    // reason the row parked may still be present) and is NOT a caller error —
    // the online/interval triggers retry. Anything else is a real bug (fail loud).
    try {
      await this.drain();
    } catch (ex) {
      if (!this.hooks.isNetworkError(ex)) throw ex;
    }
    // A retry that LANDED was GC'd out of the queue in this same drain — return
    // its terminal snapshot rather than undefined (same contract as enqueue()).
    return this._terminalRow(id);
  }

  /**
   * Permanently drop a parked row — the human chose to abandon this captured
   * write. Destructive: the clinical write it held is gone. The caller (the
   * popover) gates this behind an explicit confirm.
   */
  async discard(id) {
    await this.store.remove(id);
    await this._fireCounts();
  }

  // ── triggers ────────────────────────────────────────────────────────────────
  // "connectivity came back" (the host's subscribeOnline hook — a window
  // 'online' event on the web) → drain; a 20s interval while the queue is
  // non-empty → drain. (enqueue() already drains immediately when isOnline(), so
  // there is ONE write path for online and offline.) Idempotent to start twice.
  start() {
    if (!this._offOnline) {
      this._offOnline = this.hooks.subscribeOnline(() => { this.drain().catch(() => {}); }) || null;
    }
    if (!this._interval && typeof setInterval !== 'undefined') {
      this._interval = setInterval(() => {
        // Re-drain while ANYTHING is unfinished: 'queued' rows waiting to send OR
        // an 'inflight' row STRANDED by a tab crash/close between its inflight
        // patch and its terminal patch. Gating on 'queued' alone left a stranded
        // inflight row un-drained forever — the chip stuck "Syncing… 1 pending"
        // with no Retry (M-27). _drainOnce's stale-inflight sweep reverts such a
        // row to 'queued' and replays it (idempotent by construction).
        Promise.all([this.store.byStatus('queued'), this.store.byStatus('inflight')])
          .then(([queued, inflight]) => {
            if ((queued.length || inflight.length) && this.hooks.isOnline()) {
              this.drain().catch(() => {});
            }
          }).catch(() => {});
      }, IDLE_INTERVAL_MS);
      // Don't keep a Node process (or the harness) alive on our account.
      if (this._interval && typeof this._interval.unref === 'function') this._interval.unref();
    }
    // INITIAL DRAIN on start: a row stranded 'inflight' by a PRIOR session's crash
    // (or a 'queued' row persisted from a previous session) must replay on this
    // boot without waiting for an 'online' event or the first 20s interval tick
    // (M-27). Fire-and-forget: offline it no-ops at the auth/reachability gate.
    if (this.hooks.isOnline()) {
      this.drain().catch(() => {});
    }
    return this;
  }

  stop() {
    if (this._offOnline) { this._offOnline(); this._offOnline = null; }
    if (this._interval) { clearInterval(this._interval); this._interval = null; }
  }
}
